import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useOptimizedSearchSelectors } from '@/hooks/useOptimizedSearchSelectors';
import { useAppSelector } from '@/redux/hooks';
import { getSearchPageSize } from '@/redux/modules/pagination';
import {
    getSelectedCategoryFacetValue,
    getSelectedFacetOptionsById,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import { parseQueryParams } from '@/utils/queryParams';
import { buildSearchQuery } from '@/utils/search/searchQuery';

import { allSearchPageFilterChipsAtom, keywordFilterChipsAtom, visibleFilterChipsAtom } from '../atoms/searchPageAtoms';
import { FacetChipOption, SearchFacetItem } from '../types';

export const useGetAppliedFilterChipValues = () => {
    const setAllSearchPageFilterChips = useSetAtom(allSearchPageFilterChipsAtom);
    const setKeywordFilterChips = useSetAtom(keywordFilterChipsAtom);
    const setVisibleFilterChips = useSetAtom(visibleFilterChipsAtom);

    const { pathname, search } = useLocation();
    const queryParams = parseQueryParams(search);
    const pageSize = useAppSelector(getSearchPageSize);

    // Use optimized selectors to batch Redux calls and reduce re-renders
    const { archivedSearch, rawFacets, searchState } = useOptimizedSearchSelectors(queryParams, pathname);
    const { isInitialLoading, submitted: isLoadingSearchResults } = searchState;

    const selectedSaleTypeOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, archivedSearch, FacetTypes.SALE_TYPE_ID)
    );

    const selectedAuctionHouseOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, archivedSearch, FacetTypes.AUCTION_HOUSE_ID)
    );
    const selectedMaterialsAndTechniquesOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, archivedSearch, FacetTypes.MATERIALS_AND_TECHNIQUES_ID)
    );

    const selectedStylePeriodOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, archivedSearch, FacetTypes.STYLE_PERIOD_ID)
    );

    const selectedCreatorOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, archivedSearch, FacetTypes.CREATOR_ID)
    );

    const selectedShippingOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, archivedSearch, FacetTypes.SHIPPING)
    );

    const selectedTopRatedOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, archivedSearch, FacetTypes.TOP_RATED_ID)
    );

    const selectedCountryOptions = useAppSelector((state) =>
        getSelectedFacetOptionsById(state, archivedSearch, FacetTypes.COUNTRY_CODE_ID)
    );

    const selectedOriginCategory = useAppSelector((state) =>
        getSelectedCategoryFacetValue(state, archivedSearch, FacetTypes.ORIGIN_ID)
    );

    const selectedL1Category = useAppSelector((state) =>
        getSelectedCategoryFacetValue(state, archivedSearch, FacetTypes.CATEGORY_ID)
    );

    const convertOptionToChipValue = useCallback((options: FacetOption[], facetId: FacetTypes): FacetChipOption[] => {
        return options.map((option) => ({
            id: option.id,
            label: option.name,
            value: facetId,
        }));
    }, []);

    const getSelectedNestedFacets = useCallback((facet: SearchFacetItem | undefined): FacetOption[] => {
        const selected: FacetOption[] = [];
        if (facet.selected) {
            selected.push(facet);
        }

        // Recursively check categories
        if (facet.categories) {
            facet.categories.forEach((category) => {
                selected.push(...getSelectedNestedFacets(category));
            });
        }

        return selected;
    }, []);

    const originChips = useMemo(() => {
        // Only include origin chips when there's actually an origin query parameter
        // (i.e., when an origin filter has been applied), not just because we're on a page with origin context
        const currentOriginId = queryParams['origin_id'];
        if (!selectedOriginCategory || !currentOriginId) {
            return [];
        }
        return convertOptionToChipValue(getSelectedNestedFacets(selectedOriginCategory), FacetTypes.ORIGIN_ID);
    }, [selectedOriginCategory, convertOptionToChipValue, getSelectedNestedFacets, queryParams]);

    const l1CategoryChips = useMemo(() => {
        // Only include category chips when there's actually a category_id query parameter
        // (i.e., when a category filter has been applied), not just because we're on a category page
        const currentCategoryId = queryParams[paramTypes.CATEGORY_ID];

        if (!selectedL1Category || !currentCategoryId) {
            return [];
        }
        return convertOptionToChipValue(getSelectedNestedFacets(selectedL1Category), FacetTypes.CATEGORY_ID);
    }, [selectedL1Category, convertOptionToChipValue, getSelectedNestedFacets, queryParams]);

    const getFilterTermChipsValues = useCallback((): FacetChipOption[] => {
        const filterTermQuery = queryParams[paramTypes.FILTER_TERM] || null;
        const filterTerms: string[] = filterTermQuery ? filterTermQuery.split(',') : [];

        const filterTermChips: FacetChipOption[] = filterTerms.map((term) => ({
            label: term,
            value: FacetTypes.KEYWORD_ID,
        }));

        return filterTermChips;
    }, [queryParams]);

    useEffect(() => {
        if (isLoadingSearchResults || isInitialLoading) {
            return;
        }

        const query = buildSearchQuery({
            pathname,
            queryParams,
            rawFacets,
            searchPageSize: pageSize,
            similarItems: true,
        });

        const currentBidFilterChip = archivedSearch
            ? query?.ranges?.hammerPrice && {
                  label: `Sold Price: $${query.ranges.hammerPrice.min} - $${query.ranges.hammerPrice.max}`,
                  value: FacetTypes.HAMMER_PRICE_ID,
              }
            : query?.ranges?.currentBid && {
                  label: `Current Bid: $${query.ranges.currentBid.min} - $${query.ranges.currentBid.max}`,
                  value: FacetTypes.CURRENT_BID_ID,
              };

        const priceRangeFilterChip = query?.ranges?.estimate
            ? {
                  label: `Price Estimate: $${query.ranges.estimate.min} - $${query.ranges.estimate.max}`,
                  value: FacetTypes.ESTIMATED_PRICE_ID,
              }
            : null;

        const filterTermChipsFormatted = getFilterTermChipsValues();

        // Only include creator chips when there's actually a creator query parameter
        const hasCreatorFilter = queryParams['creator'] || queryParams['creator_id'];
        const creatorChips = hasCreatorFilter
            ? convertOptionToChipValue(selectedCreatorOptions, FacetTypes.CREATOR_ID)
            : [];

        const visibleFilterChipsFormatted = [
            currentBidFilterChip,
            priceRangeFilterChip,
            ...filterTermChipsFormatted,
            ...convertOptionToChipValue(selectedSaleTypeOptions, FacetTypes.SALE_TYPE_ID),
            ...convertOptionToChipValue(selectedAuctionHouseOptions, FacetTypes.AUCTION_HOUSE_ID),
            ...convertOptionToChipValue(selectedMaterialsAndTechniquesOptions, FacetTypes.MATERIALS_AND_TECHNIQUES_ID),
            ...convertOptionToChipValue(selectedStylePeriodOptions, FacetTypes.STYLE_PERIOD_ID),
            ...creatorChips,
            ...originChips,
        ].filter((chip) => Boolean(chip));

        const allFilterChipsFormatted = [
            ...l1CategoryChips,
            ...visibleFilterChipsFormatted,
            ...convertOptionToChipValue(selectedShippingOptions, FacetTypes.SHIPPING),
            ...convertOptionToChipValue(selectedTopRatedOptions, FacetTypes.TOP_RATED_ID),
            ...convertOptionToChipValue(selectedCountryOptions, FacetTypes.COUNTRY_CODE_ID),
        ].filter((chip) => Boolean(chip));

        setAllSearchPageFilterChips(allFilterChipsFormatted);
        setKeywordFilterChips(filterTermChipsFormatted);
        setVisibleFilterChips(visibleFilterChipsFormatted);
    }, [
        isLoadingSearchResults,
        isInitialLoading,
        archivedSearch,
        rawFacets,
        pathname,
        queryParams,
        pageSize,
        getFilterTermChipsValues,
        convertOptionToChipValue,
        selectedSaleTypeOptions,
        selectedAuctionHouseOptions,
        selectedMaterialsAndTechniquesOptions,
        selectedStylePeriodOptions,
        selectedCreatorOptions,
        originChips,
        selectedShippingOptions,
        selectedTopRatedOptions,
        selectedCountryOptions,
        setAllSearchPageFilterChips,
        setKeywordFilterChips,
        setVisibleFilterChips,
        l1CategoryChips,
    ]);
};
